import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { toast } from 'react-toastify';
import { 
  FaUpload, FaFilePdf, FaImage, FaTrash, FaEye,
  FaCheckCircle, FaTimes, FaClock, FaFileAlt,
  FaWeightHanging, FaGasPump, FaClipboardList,
  FaUser, FaCalendarAlt, FaInfoCircle, FaDownload,
  FaTruck, FaDollarSign, FaRoad, FaCalculator
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';

function SubbieUploads({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [subbieId, setSubbieId] = useState(null);
  const [user, setUser] = useState({});
  
  // Upload form state
  const [formData, setFormData] = useState({
    upload_type: 'pod',
    volume_mt: '',
    fuel_liters: '',
    fuel_odometer: '',
    fuel_total_cost: '',
    notes: '',
    file: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Auto-calculate cost per liter
  const getCostPerLiter = () => {
    const liters = parseFloat(formData.fuel_liters);
    const totalCost = parseFloat(formData.fuel_total_cost);
    if (liters > 0 && totalCost > 0) {
      return (totalCost / liters).toFixed(2);
    }
    return '0.00';
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    getSubbieId(userData);
  }, []);

  const getSubbieId = async (userData) => {
    try {
      let savedId = localStorage.getItem('subbieId');
      if (savedId) {
        setSubbieId(savedId);
        fetchData(savedId);
        return;
      }

      const response = await fetch(`${API_URL}/subcontractors?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.subcontractors && data.subcontractors.length > 0) {
        const id = data.subcontractors[0].id;
        setSubbieId(id);
        localStorage.setItem('subbieId', id);
        fetchData(id);
      } else {
        toast.error('No subcontractor profile found');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching subbie ID:', err);
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  const fetchData = async (id) => {
    setLoading(true);
    try {
      // Fetch uploads
      const uploadsRes = await fetch(`${API_URL}/subbie/uploads/${id}`);
      const uploadsData = await uploadsRes.json();
      if (uploadsData.success) {
        setUploads(uploadsData.uploads || []);
      }

      // Fetch active jobs for this subbie
      const jobsRes = await fetch(`${API_URL}/subbie/all-requirements/${id}`);
      const jobsData = await jobsRes.json();
      if (jobsData.success) {
        const activeJobs = jobsData.requirements?.filter(r => 
          r.status === 'approved' || r.status === 'accepted' || r.status === 'assigned'
        ) || [];
        setJobs(activeJobs);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload JPEG, PNG, or PDF files only');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setFormData(prev => ({ ...prev, file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedJob) {
      toast.error('Please select a job');
      return;
    }
    if (!formData.file) {
      toast.error('Please select a file to upload');
      return;
    }

    // Validate fuel fields if fuel type
    if (formData.upload_type === 'fuel') {
      if (!formData.fuel_liters || !formData.fuel_odometer || !formData.fuel_total_cost) {
        toast.error('Please fill in all fuel details');
        return;
      }
    }

    setSubmitting(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('subcontractor_id', subbieId);
      formDataObj.append('job_id', selectedJob);
      formDataObj.append('job_requirement_id', selectedJob);
      formDataObj.append('upload_type', formData.upload_type);
      formDataObj.append('volume_mt', formData.volume_mt || '');
      formDataObj.append('fuel_liters', formData.fuel_liters || '');
      formDataObj.append('fuel_odometer', formData.fuel_odometer || '');
      formDataObj.append('fuel_total_cost', formData.fuel_total_cost || '');
      formDataObj.append('fuel_cost_per_liter', getCostPerLiter());
      formDataObj.append('notes', formData.notes || '');
      formDataObj.append('file', formData.file);

      const response = await fetch(`${API_URL}/subbie/uploads`, {
        method: 'POST',
        body: formDataObj
      });

      const data = await response.json();
      if (data.success) {
        toast.success('✅ Upload submitted successfully! Waiting for admin verification.');
        setShowUploadModal(false);
        setFormData({
          upload_type: 'pod',
          volume_mt: '',
          fuel_liters: '',
          fuel_odometer: '',
          fuel_total_cost: '',
          notes: '',
          file: null
        });
        setSelectedJob(null);
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to upload');
      }
    } catch (err) {
      console.error('Error uploading:', err);
      toast.error('Failed to upload');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewFile = (fileUrl) => {
    if (!fileUrl) {
      toast.error('No file to view');
      return;
    }
    window.open(`API_BASE_URL${fileUrl}`, '_blank');
  };

  const handleDownloadFile = (fileUrl, fileName) => {
    if (!fileUrl) {
      toast.error('No file to download');
      return;
    }
    const link = document.createElement('a');
    link.href = `API_BASE_URL${fileUrl}`;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'verified': 'bg-green-100 text-green-600',
      'rejected': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'pod': return <FaFileAlt className="text-blue-500" />;
      case 'weighbridge': return <FaWeightHanging className="text-purple-500" />;
      case 'fuel': return <FaGasPump className="text-orange-500" />;
      case 'check_sheet': return <FaClipboardList className="text-teal-500" />;
      default: return <FaFileAlt className="text-gray-500" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'pod': 'POD',
      'weighbridge': 'Weighbridge',
      'fuel': 'Fuel Receipt',
      'check_sheet': 'Check Sheet'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="uploads" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading uploads...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  return (
    <SubbieLayout activeTab="uploads" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            📤 Uploads
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            Upload PODs, weighbridge tickets, fuel receipts, and check sheets
          </p>
        </div>
        <button
          onClick={() => {
            setShowUploadModal(true);
            setFormData({
              upload_type: 'pod',
              volume_mt: '',
              fuel_liters: '',
              fuel_odometer: '',
              fuel_total_cost: '',
              notes: '',
              file: null
            });
          }}
          className="px-4 py-2 rounded-lg text-white font-medium transition-colors flex items-center gap-2"
          style={{ backgroundColor: '#00A86B' }}
        >
          <FaUpload size={14} /> New Upload
        </button>
      </div>

      {/* Uploads List */}
      {uploads.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">📤</div>
          <h3 className="text-xl font-semibold" style={{ color: '#0A1929' }}>No Uploads Yet</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            Upload PODs, weighbridge tickets, fuel receipts, and more for your active jobs.
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="mt-4 px-6 py-2 rounded-lg text-white text-sm font-medium transition-colors"
            style={{ backgroundColor: '#00A86B' }}
          >
            <FaUpload className="inline mr-2" size={14} /> Upload Now
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {uploads.map((upload) => (
            <div key={upload.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getTypeIcon(upload.upload_type)}
                    <span className="font-medium" style={{ color: '#0A1929' }}>
                      {getTypeLabel(upload.upload_type)}
                    </span>
                    <span className={getStatusBadge(upload.status)}>
                      {upload.status || 'pending'}
                    </span>
                    {upload.verified && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <FaCheckCircle size={12} /> Verified
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: '#4A5568' }}>
                    <strong>Job:</strong> {upload.jobs?.job_number || 'N/A'}
                    {upload.volume_mt && ` • <strong>Volume:</strong> ${upload.volume_mt} MT`}
                    {upload.fuel_liters && ` • <strong>Fuel:</strong> ${upload.fuel_liters}L`}
                    {upload.fuel_odometer && ` • <strong>Odometer:</strong> ${upload.fuel_odometer} km`}
                    {upload.fuel_total_cost && ` • <strong>Cost:</strong> R${upload.fuel_total_cost}`}
                    {upload.fuel_cost_per_liter && ` • <strong>R/L:</strong> R${upload.fuel_cost_per_liter}`}
                  </p>
                  {upload.notes && (
                    <p className="text-sm mt-1" style={{ color: '#4A5568' }}>
                      <span className="font-medium">Notes:</span> {upload.notes}
                    </p>
                  )}
                  <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                    Uploaded: {formatDate(upload.created_at)}
                    {upload.verified_at && ` • Verified: ${formatDate(upload.verified_at)}`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {upload.file_url && (
                    <>
                      <button 
                        onClick={() => handleViewFile(upload.file_url)}
                        className="flex items-center gap-2 px-3 py-1 border rounded-lg text-sm hover:border-blue-500 hover:text-blue-500 transition-colors"
                        style={{ borderColor: '#E8ECEF' }}
                      >
                        <FaEye size={14} /> View File
                      </button>
                      <button 
                        onClick={() => handleDownloadFile(upload.file_url, upload.file_name)}
                        className="flex items-center gap-2 px-3 py-1 border rounded-lg text-sm hover:border-green-500 hover:text-green-500 transition-colors"
                        style={{ borderColor: '#E8ECEF' }}
                      >
                        <FaDownload size={14} /> Download
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowUploadModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold" style={{ color: '#0A1929' }}>📤 New Upload</h2>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                  Select Job <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedJob || ''}
                  onChange={(e) => setSelectedJob(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                  style={{ borderColor: '#E8ECEF' }}
                  required
                >
                  <option value="">Select an active job...</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.jobs?.id || job.id}>
                      {job.jobs?.job_number} - {job.jobs?.commodity} ({job.jobs?.origin} → {job.jobs?.destination})
                    </option>
                  ))}
                </select>
                {jobs.length === 0 && (
                  <p className="text-xs text-yellow-600 mt-1">No active jobs available for uploads.</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                  Upload Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="upload_type"
                  value={formData.upload_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                  style={{ borderColor: '#E8ECEF' }}
                  required
                >
                  <option value="pod">📄 POD (Proof of Delivery)</option>
                  <option value="weighbridge">⚖️ Weighbridge Ticket</option>
                  <option value="fuel">⛽ Fuel Receipt</option>
                  <option value="check_sheet">📋 Check Sheet</option>
                </select>
              </div>

              {/* Volume Field - for POD and Weighbridge */}
              {(formData.upload_type === 'pod' || formData.upload_type === 'weighbridge') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                    Volume (MT) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="volume_mt"
                    step="0.01"
                    value={formData.volume_mt}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                    style={{ borderColor: '#E8ECEF' }}
                    placeholder="e.g. 34.5"
                    required={formData.upload_type === 'pod' || formData.upload_type === 'weighbridge'}
                  />
                </div>
              )}

              {/* Fuel Receipt Fields */}
              {formData.upload_type === 'fuel' && (
                <div className="border rounded-lg p-4 mb-4" style={{ borderColor: '#E8ECEF' }}>
                  <h3 className="font-semibold mb-3" style={{ color: '#0A1929' }}>
                    <FaGasPump className="inline mr-2" style={{ color: '#00A86B' }} />
                    Fuel Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                        Liters <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="fuel_liters"
                        step="0.01"
                        value={formData.fuel_liters}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                        style={{ borderColor: '#E8ECEF' }}
                        placeholder="e.g. 450.5"
                        required={formData.upload_type === 'fuel'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                        Odometer Reading (km) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="fuel_odometer"
                        step="1"
                        value={formData.fuel_odometer}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                        style={{ borderColor: '#E8ECEF' }}
                        placeholder="e.g. 12450"
                        required={formData.upload_type === 'fuel'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                        Total Cost (R) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="fuel_total_cost"
                        step="0.01"
                        value={formData.fuel_total_cost}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                        style={{ borderColor: '#E8ECEF' }}
                        placeholder="e.g. 4250.00"
                        required={formData.upload_type === 'fuel'}
                      />
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between border border-gray-200">
                      <div>
                        <label className="block text-sm font-medium" style={{ color: '#4A5568' }}>
                          Cost per Liter (R)
                        </label>
                        <p className="text-2xl font-bold" style={{ color: '#00A86B' }}>
                          R{getCostPerLiter()}
                        </p>
                      </div>
                      <FaCalculator className="text-3xl text-gray-400" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    <FaInfoCircle className="inline mr-1" size={12} />
                    Cost per liter is calculated automatically from Total Cost ÷ Liters
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                  style={{ borderColor: '#E8ECEF' }}
                  rows="2"
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1" style={{ color: '#4A5568' }}>
                  File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green"
                  style={{ borderColor: '#E8ECEF' }}
                  required
                />
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  Accepted formats: PDF, JPEG, PNG (max 10MB)
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-800 flex items-start gap-2">
                  <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Important:</strong> No invoice will be paid without original POD being presented/delivered at accounts department.
                  </span>
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
                  style={{ borderColor: '#E8ECEF' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#00A86B' }}
                >
                  <FaUpload size={14} />
                  {submitting ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SubbieLayout>
  );
}

export default SubbieUploads;
